import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ClientService } from '../client/client.service';

/**
 * Network reachability tracker. The truth comes from three sources:
 *  - browser online/offline events (fast, but scanners often keep
 *    navigator.onLine true on a dead Wi-Fi link),
 *  - actual request outcomes reported by IoService (status 0 = unreachable),
 *  - a recovery probe against the API origin while offline (the app's own
 *    origin can't be probed — the PWA service worker answers from cache).
 *
 * Consumers (the transfer screen's offline overlay) subscribe to make a lost
 * connection unmissable while scanning.
 */
@Injectable({
  providedIn: 'root'
})
export class ConnectivityService {

  private online = new BehaviorSubject<boolean>(true);
  private probeTimer: any = null;
  private probing = false;

  constructor(
    private clientService: ClientService,
  ) {
    window.addEventListener('offline', () => this.markOffline());
    window.addEventListener('online', () => this.probeNow());
  }

  isOnline(): boolean {
    return this.online.value;
  }

  onlineObservable(): Observable<boolean> {
    return this.online.asObservable();
  }

  markOffline() {
    if (this.online.value) {
      this.online.next(false);
    }
    this.startProbe();
  }

  markOnline() {
    this.stopProbe();
    if (!this.online.value) {
      this.online.next(true);
    }
  }

  private startProbe() {
    if (this.probeTimer) {
      return;
    }
    this.probeTimer = setInterval(() => this.probeNow(), 3000);
  }

  private stopProbe() {
    if (this.probeTimer) {
      clearInterval(this.probeTimer);
      this.probeTimer = null;
    }
  }

  async probeNow() {
    if (this.probing) {
      return;
    }
    this.probing = true;
    try {
      // Any HTTP response (even a 404) proves the API origin is reachable;
      // only a network-level failure throws.
      await fetch(this.clientService.getServerPath() + '?connectivity=' + Date.now(), {
        method: 'GET',
        cache: 'no-store',
      });
      this.markOnline();
    } catch (e) {
      this.markOffline();
    } finally {
      this.probing = false;
    }
  }
}
